import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import withPage from '../page';
import { Helpers } from '../core';

import SearchPageContentsContainer from './components/search-page-contents';
import SearchPageProducts from './components/search-page-products';
import SearchStoreSupport from './components/search-store-support';
import useHawksearchQueryStringListener from './useHawksearchQueryStringListener';
import SearchPageTemplate from './components/search-page-template';
import SearchPageBreadcrumb from './components/search-page-breadcrumb';
import useUserData, { getPStoreID } from '../hooks/useUserData';

const VANITY_URL = 'sitesearch';
const VALID_TYPE_QUERY_STRINGS = ['support', 'content'];
const COMMON_GTM_TAB_PROPS = {
    gtmCategory: 'linkClick',
    gtmId: 'search-tab',
};
// TODO: change tabs.title back to <h2> once there is support for it on stellar side
const getDefaultTabs = () => [
    {
        id: 'EXPLORE',
        name: 'content',
        queryStringValue: 'content',
        tab: {
            title: 'Explore',
            ...COMMON_GTM_TAB_PROPS,
            gtmValue: 'explore',
        },
        component: SearchPageContentsContainer,
        gtmConfig: {
            loadMore: {
                gtmId: 'explore',
            },
            linkClick: {
                gtmId: 'explore',
            },
        },
        sortOptions: [
            { label: 'View count', field: 'view_count', order: 'desc', field: 'view_count' },
            { label: 'Title', field: 'blog_title', order: 'asc', field: 'blog_title' },
            { label: 'Newest to oldest', field: 'blog_date', order: 'desc', field: 'blog_date' },
        ],
    },
    {
        id: 'PRODUCTS',
        name: 'products',
        tab: {
            title: 'Shop',
            ...COMMON_GTM_TAB_PROPS,
            gtmValue: 'shop',
        },
        gtmConfig: {
            loadMore: {
                gtmId: 'shop',
            },
            linkClick: {
                gtmId: 'shop',
            },
        },
        component: SearchPageProducts,
    },
    {
        id: 'STORE SUPPORT',
        name: 'support',
        tab: {
            title: 'Support',
            ...COMMON_GTM_TAB_PROPS,
            gtmValue: 'support',
        },
        queryStringValue: 'support',
        gtmConfig: {
            loadMore: {
                gtmId: 'support',
            },
            linkClick: {
                gtmId: 'support',
            },
        },
        component: SearchStoreSupport,
    },
];

const DEFAULT_STARTING_TAB = 'products';

const getTabs = Helpers.memoize(srpTabs => {
    const defaultTabs = getDefaultTabs();
    const filteredTabs =
        Array.isArray(srpTabs) &&
        srpTabs.length > 0 &&
        srpTabs.map(tab => ({ ...defaultTabs.find(t => t.id === tab) }));

    //fallback to all tabs if configured incorrectly and no tabs match
    return filteredTabs && filteredTabs.length > 0 ? filteredTabs : defaultTabs;
}, srpTabs => Array.isArray(srpTabs) ? srpTabs.join('-') : '');

const getSearchKeywordAndTypeFromQs = queryString => {
    try{
        const searchObj = Helpers.getSearch(queryString);
        const searchType = searchObj?.type;
        const searchKeyword = searchObj?.keyword;

        return { searchType, searchKeyword }
    }catch(e){}

    return {}
}

const SearchPageContainer = ({ slugInfo, receiveSlugInfo, search, ...rest }) => {
    const { hawksearchResults, srpTabs } = (slugInfo && slugInfo.components) || {};
    const { type } = hawksearchResults || {};
    const displayTabs = getTabs(srpTabs);
    const tabType = type || (search && search.type) || DEFAULT_STARTING_TAB;
    const { products, content } = hawksearchResults || {};
    const contentQuery = content && content.query;
    const productQuery = products && products.query;
    const { userData } = useUserData();
    const pStoreID = getPStoreID(userData);
    const [searchState, setSearchState] = useState({
        tab: tabType || DEFAULT_STARTING_TAB,
        activeIndex: Math.max(
            displayTabs.findIndex(tab => tab.name === tabType),
            0
        ),
    });
    const { tab, searchKey, activeIndex } = searchState || {};
    let currentTab = displayTabs.find(({ name }) => name === tab);
    let { sortOptions, component: Component } = currentTab || {};
    const history = useHistory();
    const location = useLocation();
    const queryString = location?.search;
    const tabName = currentTab && currentTab.name;
    
    useEffect(() => {
        // const searchObj = Helpers.getSearch(queryString);
        // const searchType = searchObj?.type;
        // const searchKeyword = searchObj?.keyword;
        const { searchType, searchKeyword } = getSearchKeywordAndTypeFromQs(queryString) || {};
        try {
            if (tab !== searchType || searchKey !== searchKeyword) {
                let activeIndex = displayTabs.findIndex(tab => tab.name === searchType);
                let newTab = searchType;

                if (activeIndex < 0) {
                    activeIndex = displayTabs.findIndex(tab => tab.name === DEFAULT_STARTING_TAB);
                    newTab = displayTabs[activeIndex] && displayTabs[activeIndex].name;
                }

                if (newTab) {
                    setSearchState({
                        tab: newTab,
                        searchKey: searchKeyword,
                        activeIndex,
                    });
                }
            }
        } catch (e) {}
    }, [queryString]);

    const switchTabs = index => {
        const activeTab = displayTabs[index];
        let { name } = activeTab || {};
        let tabQuery = hawksearchResults && hawksearchResults[name] && hawksearchResults[name].query;
        try {
            const query = window.location.search;
            const searchObj = typeof query === 'string' ? Helpers.getSearch(query) : '';
            const { keyword } = searchObj || {};
            if(typeof tabQuery === 'string' && tabQuery.length > 1 && new RegExp(`(^|\\&|\\?)keyword=${keyword}($|\\&|\\?)`, 'ig').test(tabQuery)) {
                return history.push(
                    `${process.env.BASENAME}/sitesearch${tabQuery[0]=== '?' ? tabQuery : '?'+tabQuery}`
                );
            }
            if (name) {
                const query = window.location.search;
                const searchObj = typeof query === 'string' ? Helpers.getSearch(query) : '';
                const { keyword } = searchObj || {};
                let qs = keyword && keyword.length ? `keyword=${keyword}` : '';
                if (pStoreID && pStoreID.length) {
                    let pstoreQs = `pStoreID=${pStoreID}`;
                    qs = qs && qs.length > 0 ? qs + '&' + pstoreQs : pstoreQs;
                }

                const currentQueryString = ((qs || '') + location.hash)
                    .replace(/^\?/, '')
                    .replace(/\&\&/, '&')
                    .replace(/\&$/, '');
                const isProductTab = name === 'products';
                // TODO: Need to sort this similar to the other functions
                const finalQueryString = `${!isProductTab ? 'type=' + name : ''}${
                    currentQueryString && currentQueryString.length
                        ? (!isProductTab ? '&' : '') + currentQueryString
                        : ''
                }`;
                history.push(
                    `${process.env.BASENAME}/sitesearch${finalQueryString.length > 0 ? '?' + finalQueryString : ''}`
                );
            }
        } catch (e) {}
    };
    const srpTabKeys = Array.isArray(srpTabs) && srpTabs.join('-');
    // sets the active index after the tab arrangement has been fetched for pstore users
    useEffect(() => {
        const { searchKeyword } = getSearchKeywordAndTypeFromQs(queryString) || {};
        const currTabIndex = srpTabs && displayTabs.findIndex(tab => tab.name === tabType);

        // redirect to product or 1st tab if tab unavailable in pstore
        if(srpTabs && currTabIndex < 0) {
            const switchIndex = Math.max(displayTabs.findIndex(tab => tab.name === DEFAULT_STARTING_TAB), 0);
            switchTabs(switchIndex);
        }
        else if (srpTabs && currTabIndex !== activeIndex) {
            const currTab = displayTabs[currTabIndex];
            currTab &&
                setSearchState({
                    tab: currTab.name,
                    searchKey: searchKeyword,
                    activeIndex: Math.max(currTabIndex, 0),
                });
        }
    }, [srpTabKeys]);

    // updates hawksearch results when query string changes
    useHawksearchQueryStringListener();

    return (
        <>
            <SearchPageBreadcrumb type={tabName} />
            <div className="search-page-container" id="pageContent">
                <SearchPageTemplate
                    onSwitchTabs={switchTabs}
                    tabs={displayTabs.length > 1 ? displayTabs : null}
                    currentTab={currentTab}
                    activeIndex={activeIndex}
                    searchKey={searchKey}
                    sortOptions={sortOptions}
                    type={tabName}
                >
                    {props => Component && <Component {...props} />}
                </SearchPageTemplate>
            </div>
        </>
    );
};

export default withPage(SearchPageContainer, {
    showWordCloud: true,
    useV2: true,
    useGraphQL: true,
    disablePlaceholder: true,
    derivedPath: (match, search, location) => {
        const { params, url } = match;
        const { dir } = params;
        const [baseName, vanitySlug] = url.split(dir);
        // return `${dir}${vanitySlug}`;
        return `${dir}${location && location.search ? location.search.replace(/^\?/, '/') : ''}`;
    },
    disableBreadcrumbs: true,
});
